import { type Edge, MarkerType, type Node } from '@xyflow/react';
import dagre from 'dagre';

import { ACCENT } from '../components/MindMapNode';

import type { MindMapNodeData, SubItemData } from '../components/MindMapNode';
import type { DashboardConfig, Direction, TreeNodeMetric } from '@repo/shared-types';

// ─── defaults ────────────────────────────────────────────────────────────────
const DEFAULT_WIDTH = 240;
const DEFAULT_HEIGHT = 108;

// ─── helpers ─────────────────────────────────────────────────────────────────
function pct(part: number, total: number): string {
  if (total === 0) return '';
  return `${Math.round((part / total) * 100)}%`;
}
function pctNum(part: number, total: number): number {
  if (total === 0) return 0;
  return Math.round((part / total) * 100);
}

type CountExtractor = (metric: TreeNodeMetric) => number;

// ─── build a single branch (shared between normal + split mode) ──────────────
function buildBranch(
  treeConfig: DashboardConfig['tree'],
  data: Record<string, TreeNodeMetric>,
  selectedClassification: string | null,
  getCount: CountExtractor,
  idPrefix: string,
  showDirection: boolean,
) {
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: 'LR', ranksep: 260, nodesep: 60, edgesep: 10 });

  for (const nodeCfg of treeConfig.nodes) {
    const w = nodeCfg.layout?.width ?? DEFAULT_WIDTH;
    const h = nodeCfg.layout?.height ?? DEFAULT_HEIGHT;
    g.setNode(nodeCfg.id, { width: w, height: h });
  }
  for (const edge of treeConfig.edges) {
    g.setEdge(edge.source, edge.target);
  }
  dagre.layout(g);

  const nodeMap = new Map(treeConfig.nodes.map((n) => [n.id, n]));

  // Build edge-based parent/children maps for complementary resolution
  const parentIdByNodeId = new Map<string, string>();
  const childIdsByParent = new Map<string, string[]>();
  for (const edge of treeConfig.edges) {
    parentIdByNodeId.set(edge.target, edge.source);
    const arr = childIdsByParent.get(edge.source) ?? [];
    arr.push(edge.target);
    childIdsByParent.set(edge.source, arr);
  }

  // Resolve effective metric key for this node.
  // - 2+ keys → composite (e.g. union/intersection) is computed under composite_{id}
  // - exactly 1 key in metricKeys → behaves identically to single metricKey
  //   (the editor moves the value into metricKeys[0] on "Add metric", leaving
  //   metricKey undefined — that single-element array would otherwise miss
  //   both code paths and render 0)
  // - else fall back to metricKey
  function resolveMetric(nodeCfg: (typeof treeConfig.nodes)[number]): TreeNodeMetric {
    const zero = { count: 0, change: null, inbound: 0, outbound: 0 } as TreeNodeMetric;
    if (nodeCfg.metricKeys && nodeCfg.metricKeys.length >= 2) {
      return data[`composite_${nodeCfg.id}`] ?? zero;
    }
    const singleKey = nodeCfg.metricKey ?? nodeCfg.metricKeys?.[0];
    return singleKey ? (data[singleKey] ?? zero) : zero;
  }

  // Resolve count for a node — complementary nodes get parent − siblings sum
  function resolveCount(nodeCfg: (typeof treeConfig.nodes)[number]): number {
    if (!nodeCfg.complementary) {
      return getCount(resolveMetric(nodeCfg));
    }
    const parentId = parentIdByNodeId.get(nodeCfg.id);
    if (!parentId) return 0;
    const parentCfg = nodeMap.get(parentId);
    if (!parentCfg) return 0;
    const parentCount = getCount(resolveMetric(parentCfg));
    const siblingsSum = (childIdsByParent.get(parentId) ?? [])
      .filter((id) => id !== nodeCfg.id)
      .reduce((sum, sibId) => {
        const sibCfg = nodeMap.get(sibId);
        if (!sibCfg || sibCfg.complementary) return sum;
        if (!sibCfg.metricKey && !(sibCfg.metricKeys && sibCfg.metricKeys.length >= 2)) return sum;
        return sum + getCount(resolveMetric(sibCfg));
      }, 0);
    return Math.max(0, parentCount - siblingsSum);
  }

  const flowNodes: Node[] = treeConfig.nodes.map((nodeCfg) => {
    const metric = resolveMetric(nodeCfg);
    const parentMetric = nodeCfg.parentMetricKey ? data[nodeCfg.parentMetricKey] : null;
    const pos = g.node(nodeCfg.id);
    const nodeW = nodeCfg.layout?.width ?? DEFAULT_WIDTH;
    const nodeH = nodeCfg.layout?.height ?? DEFAULT_HEIGHT;
    const offsetX = nodeCfg.layout?.offsetX ?? 0;
    const offsetY = nodeCfg.layout?.offsetY ?? 0;

    const count = resolveCount(nodeCfg);
    const parentCount = parentMetric ? getCount(parentMetric) : null;

    const subItems: SubItemData[] | undefined = nodeCfg.subItems?.map((s) => {
      const sm = data[s.metricKey] ?? { count: 0, change: null, inbound: 0, outbound: 0 };
      const base = data[s.pctBaseMetricKey] ?? {
        count: 0,
        change: null,
        inbound: 0,
        outbound: 0,
      };
      return {
        label: s.label,
        count: getCount(sm),
        change: sm.change,
        pctOfParent: pctNum(getCount(sm), getCount(base)),
        colorClass: s.colorClass,
        classification: s.classification,
        metricKey: s.metricKey,
        isSelected: selectedClassification === s.classification,
      };
    });

    const isRoot = !nodeCfg.parentMetricKey;

    return {
      id: `${idPrefix}${nodeCfg.id}`,
      type: 'mindMapNode',
      position: { x: pos.x - nodeW / 2 + offsetX, y: pos.y - nodeH / 2 + offsetY },
      data: {
        label: nodeCfg.label,
        count,
        change: nodeCfg.complementary ? null : metric.change,
        inbound: nodeCfg.complementary ? 0 : metric.inbound,
        outbound: nodeCfg.complementary ? 0 : metric.outbound,
        colorClass: nodeCfg.colorClass,
        showDirection,
        classification: nodeCfg.classification,
        metricKey: nodeCfg.metricKey ?? nodeCfg.metricKeys?.[0],
        isSelected: selectedClassification === nodeCfg.classification,
        isRoot,
        pctOfParent: parentCount !== null ? pctNum(count, parentCount) : 100,
        parentCount,
        subItems,
        tooltip: nodeCfg.tooltip,
      } satisfies MindMapNodeData,
    };
  });

  const flowEdges: Edge[] = treeConfig.edges.map((e) => {
    const targetCfg = nodeMap.get(e.target);
    const sourceCfg = nodeMap.get(e.source);
    const sourceMetric = sourceCfg?.metricKey ? data[sourceCfg.metricKey] : null;
    const targetCount = targetCfg ? resolveCount(targetCfg) : null;
    const sourceCount = sourceMetric ? getCount(sourceMetric) : null;
    const label =
      targetCount !== null && sourceCount !== null ? pct(targetCount, sourceCount) : undefined;
    const accent = ACCENT[targetCfg?.colorClass ?? 'slate'] ?? '#64748b';

    return {
      id: `${idPrefix}${e.source}->${idPrefix}${e.target}`,
      source: `${idPrefix}${e.source}`,
      target: `${idPrefix}${e.target}`,
      sourceHandle: null,
      targetHandle: null,
      type: 'default',
      label: label || undefined,
      labelStyle: { fontSize: 9, fontWeight: 700, fill: accent },
      labelBgStyle: { fill: 'hsl(var(--background))', fillOpacity: 0.95 },
      labelBgPadding: [4, 3] as [number, number],
      labelBgBorderRadius: 4,
      markerEnd: { type: MarkerType.ArrowClosed, color: accent, width: 14, height: 14 },
      style: { stroke: accent, strokeWidth: 1.5, strokeOpacity: 0.6 },
    };
  });

  return { nodes: flowNodes, edges: flowEdges };
}

// ─── main export ─────────────────────────────────────────────────────────────
export function buildTreeLayoutFromConfig(
  treeConfig: DashboardConfig['tree'],
  data: Record<string, TreeNodeMetric>,
  direction: Direction,
  selectedClassification: string | null,
): { nodes: Node[]; edges: Edge[] } {
  // ── Non-split mode (INBOUND or OUTBOUND selected) ───────────────────────
  if (direction !== 'ALL') {
    return buildBranch(treeConfig, data, selectedClassification, (m) => m.count, '', false);
  }

  // ── Split mode: ALL → show Inbound + Outbound branches ─────────────────
  // A node is a root if no edges point to it
  const incoming = new Set(treeConfig.edges.map((e) => e.target));
  const rootNodeCfg = treeConfig.nodes.find((n) => !incoming.has(n.id)) || treeConfig.nodes[0];
  if (!rootNodeCfg) {
    return buildBranch(treeConfig, data, selectedClassification, (m) => m.count, '', true);
  }

  const rootMetric = rootNodeCfg.metricKey
    ? (data[rootNodeCfg.metricKey] ?? {
        count: 0,
        change: null,
        inbound: 0,
        outbound: 0,
      })
    : {
        count: 0,
        change: null,
        inbound: 0,
        outbound: 0,
      };

  const rootChildEdges = treeConfig.edges.filter((e) => e.source === rootNodeCfg.id);

  // Build inbound branch (keep root node so Dagre generates identical coordinates to INBOUND mode)
  const inboundRaw = buildBranch(
    treeConfig,
    data,
    selectedClassification,
    (m) => m.inbound,
    'in_',
    false,
  );

  // Build outbound branch (keep root node so Dagre generates identical coordinates)
  const outboundRaw = buildBranch(
    treeConfig,
    data,
    selectedClassification,
    (m) => m.outbound,
    'out_',
    false,
  );

  // Filter out the duplicated root nodes since we will draw a customized central root.
  // Only remove the actual root node by ID — do NOT use the isRoot flag here, because isRoot
  // is based on parentMetricKey being absent and can be true for non-root nodes (e.g. confirmCar),
  // which would cause them to disappear in ALL-direction (split) mode.
  const inbound = {
    nodes: inboundRaw.nodes.filter((n) => n.id !== `in_${rootNodeCfg.id}`),
    edges: inboundRaw.edges.filter(
      (e) => e.source !== `in_${rootNodeCfg.id}` && e.target !== `in_${rootNodeCfg.id}`,
    ),
  };
  const outbound = {
    nodes: outboundRaw.nodes.filter((n) => n.id !== `out_${rootNodeCfg.id}`),
    edges: outboundRaw.edges.filter(
      (e) => e.source !== `out_${rootNodeCfg.id}` && e.target !== `out_${rootNodeCfg.id}`,
    ),
  };

  // Offset outbound branch below inbound
  const inboundBounds = { minY: Infinity, maxY: -Infinity };
  for (const n of inbound.nodes) {
    const h = (n.measured?.height as number) ?? DEFAULT_HEIGHT;
    inboundBounds.minY = Math.min(inboundBounds.minY, n.position.y);
    inboundBounds.maxY = Math.max(inboundBounds.maxY, n.position.y + h);
  }
  const gap = 80;
  const inboundHeight = inboundBounds.maxY - inboundBounds.minY;
  const outboundOffsetY = inboundHeight + gap;

  for (const n of outbound.nodes) {
    n.position.y += outboundOffsetY;
  }

  // Create the root "All" node
  const rootW = rootNodeCfg.layout?.width ?? 300;
  const rootH = rootNodeCfg.layout?.height ?? DEFAULT_HEIGHT;
  const totalHeight = inboundHeight + gap + inboundHeight; // approximate
  const rootY = totalHeight / 2 - rootH / 2 + (inboundBounds.minY ?? 0);

  // Find the leftmost x of child nodes for positioning root to the left
  let minChildX = Infinity;
  for (const n of [...inbound.nodes, ...outbound.nodes]) {
    minChildX = Math.min(minChildX, n.position.x);
  }
  const rootX = minChildX - rootW - 280;

  const rootNode: Node = {
    id: 'root_all',
    type: 'mindMapNode',
    position: { x: rootX, y: rootY },
    data: {
      label: rootNodeCfg.label,
      count: rootMetric.count,
      change: rootMetric.change,
      inbound: rootMetric.inbound,
      outbound: rootMetric.outbound,
      colorClass: rootNodeCfg.colorClass,
      showDirection: true,
      classification: rootNodeCfg.classification,
      metricKey: rootNodeCfg.metricKey ?? rootNodeCfg.metricKeys?.[0],
      isSelected: selectedClassification === rootNodeCfg.classification,
      isRoot: true,
      pctOfParent: 100,
      parentCount: null,
    } satisfies MindMapNodeData,
  };

  // Create direction header nodes
  const dirNodeW = 260;
  const dirNodeH = DEFAULT_HEIGHT;
  const dirNodeX = minChildX - dirNodeW - 60;

  // Inbound center Y
  const inCenterY =
    inbound.nodes.length > 0
      ? inbound.nodes.reduce((sum, n) => sum + n.position.y, 0) / inbound.nodes.length
      : rootY - 100;

  // Outbound center Y
  const outCenterY =
    outbound.nodes.length > 0
      ? outbound.nodes.reduce((sum, n) => sum + n.position.y, 0) / outbound.nodes.length
      : rootY + 100;

  const inboundDirNode: Node = {
    id: 'dir_inbound',
    type: 'mindMapNode',
    position: { x: dirNodeX, y: inCenterY - dirNodeH / 2 },
    data: {
      label: '↓ Inbound',
      count: rootMetric.inbound,
      change: null,
      inbound: rootMetric.inbound,
      outbound: 0,
      colorClass: 'blue',
      showDirection: false,
      classification: rootNodeCfg.classification,
      metricKey: rootNodeCfg.metricKey ?? rootNodeCfg.metricKeys?.[0],
      isSelected: false,
      isRoot: false,
      pctOfParent: pctNum(rootMetric.inbound, rootMetric.count),
      parentCount: rootMetric.count,
    } satisfies MindMapNodeData,
  };

  const outboundDirNode: Node = {
    id: 'dir_outbound',
    type: 'mindMapNode',
    position: { x: dirNodeX, y: outCenterY - dirNodeH / 2 },
    data: {
      label: '↑ Outbound',
      count: rootMetric.outbound,
      change: null,
      inbound: 0,
      outbound: rootMetric.outbound,
      colorClass: 'purple',
      showDirection: false,
      classification: rootNodeCfg.classification,
      metricKey: rootNodeCfg.metricKey ?? rootNodeCfg.metricKeys?.[0],
      isSelected: false,
      isRoot: false,
      pctOfParent: pctNum(rootMetric.outbound, rootMetric.count),
      parentCount: rootMetric.count,
    } satisfies MindMapNodeData,
  };

  // Build edges from root → direction nodes → child roots
  const accentBlue = ACCENT.blue ?? '#3b82f6';
  const accentPurple = ACCENT.purple ?? '#8b5cf6';

  const splitEdges: Edge[] = [
    // Root → Inbound
    {
      id: 'root_all->dir_inbound',
      source: 'root_all',
      target: 'dir_inbound',
      type: 'default',
      label: pct(rootMetric.inbound, rootMetric.count) || undefined,
      labelStyle: { fontSize: 9, fontWeight: 700, fill: accentBlue },
      labelBgStyle: { fill: 'hsl(var(--background))', fillOpacity: 0.95 },
      labelBgPadding: [4, 3] as [number, number],
      labelBgBorderRadius: 4,
      markerEnd: { type: MarkerType.ArrowClosed, color: accentBlue, width: 14, height: 14 },
      style: { stroke: accentBlue, strokeWidth: 1.5, strokeOpacity: 0.6 },
    },
    // Root → Outbound
    {
      id: 'root_all->dir_outbound',
      source: 'root_all',
      target: 'dir_outbound',
      type: 'default',
      label: pct(rootMetric.outbound, rootMetric.count) || undefined,
      labelStyle: { fontSize: 9, fontWeight: 700, fill: accentPurple },
      labelBgStyle: { fill: 'hsl(var(--background))', fillOpacity: 0.95 },
      labelBgPadding: [4, 3] as [number, number],
      labelBgBorderRadius: 4,
      markerEnd: { type: MarkerType.ArrowClosed, color: accentPurple, width: 14, height: 14 },
      style: { stroke: accentPurple, strokeWidth: 1.5, strokeOpacity: 0.6 },
    },
  ];

  // Connect direction nodes to the first-level children in each branch
  for (const edge of rootChildEdges) {
    const targetCfg = treeConfig.nodes.find((n) => n.id === edge.target);
    const accent = ACCENT[targetCfg?.colorClass ?? 'slate'] ?? '#64748b';

    // Inbound direction → inbound child
    splitEdges.push({
      id: `dir_inbound->in_${edge.target}`,
      source: 'dir_inbound',
      target: `in_${edge.target}`,
      type: 'default',
      labelStyle: { fontSize: 9, fontWeight: 700, fill: accent },
      labelBgStyle: { fill: 'hsl(var(--background))', fillOpacity: 0.95 },
      labelBgPadding: [4, 3] as [number, number],
      labelBgBorderRadius: 4,
      markerEnd: { type: MarkerType.ArrowClosed, color: accent, width: 14, height: 14 },
      style: { stroke: accent, strokeWidth: 1.5, strokeOpacity: 0.6 },
    });

    // Outbound direction → outbound child
    splitEdges.push({
      id: `dir_outbound->out_${edge.target}`,
      source: 'dir_outbound',
      target: `out_${edge.target}`,
      type: 'default',
      labelStyle: { fontSize: 9, fontWeight: 700, fill: accent },
      labelBgStyle: { fill: 'hsl(var(--background))', fillOpacity: 0.95 },
      labelBgPadding: [4, 3] as [number, number],
      labelBgBorderRadius: 4,
      markerEnd: { type: MarkerType.ArrowClosed, color: accent, width: 14, height: 14 },
      style: { stroke: accent, strokeWidth: 1.5, strokeOpacity: 0.6 },
    });
  }

  return {
    nodes: [rootNode, inboundDirNode, outboundDirNode, ...inbound.nodes, ...outbound.nodes],
    edges: [...splitEdges, ...inbound.edges, ...outbound.edges],
  };
}
